import { useRef, useState } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from './Modal'
import { Button } from './Button'

export interface ImportReport {
  inserted: number
  updated: number
  skipped: number
  errors: { row: number; message: string }[]
}

interface ImportDialogProps {
  open: boolean
  onClose: () => void
  /** Endpoint relative to /api, e.g. '/products/import' */
  endpoint: string
  title: string
  hint: string
  onSuccess?: () => void
}

export function ImportDialog({
  open,
  onClose,
  endpoint,
  title,
  hint,
  onSuccess,
}: ImportDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const mutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData()
      fd.append('file', f)
      const res = await api.post<{ report: ImportReport }>(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data.report
    },
    onSuccess: (r) => {
      setReport(r)
      onSuccess?.()
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la import'
      toast.error(message)
    },
  })

  const reset = () => {
    setFile(null)
    setReport(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) {
      setFile(f)
      setReport(null)
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const f = e.dataTransfer.files?.[0]
    if (f) {
      setFile(f)
      setReport(null)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        report ? (
          <Button onClick={handleClose}>Închide</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Anulează
            </Button>
            <Button
              onClick={() => file && mutation.mutate(file)}
              disabled={!file}
              loading={mutation.isPending}
            >
              Importă
            </Button>
          </>
        )
      }
    >
      {!report ? (
        <div className="space-y-3">
          <p className="text-sm text-b4l-text-muted">{hint}</p>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="cursor-pointer rounded-lg border-2 border-dashed border-b4l-border bg-b4l-bg-soft p-6 text-center hover:border-b4l-green"
          >
            {file ? (
              <div>
                <div className="text-sm font-medium">{file.name}</div>
                <div className="text-xs text-b4l-text-muted">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
            ) : (
              <div className="text-sm text-b4l-text-muted">
                Apasă ca să alegi fișierul sau trage-l aici (.csv, .xlsx)
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={onPick}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded bg-b4l-green-light p-2">
              <div className="text-2xl font-semibold text-b4l-green-dark">{report.inserted}</div>
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">
                Adăugate
              </div>
            </div>
            <div className="rounded bg-b4l-blue-light p-2">
              <div className="text-2xl font-semibold text-b4l-blue-dark">{report.updated}</div>
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">
                Actualizate
              </div>
            </div>
            <div className="rounded bg-b4l-red-light p-2">
              <div className="text-2xl font-semibold text-b4l-red-dark">{report.errors.length}</div>
              <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">Erori</div>
            </div>
          </div>
          {report.errors.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-b4l-text-muted">Erori pe rânduri:</div>
              <div className="max-h-40 overflow-y-auto rounded border border-b4l-border bg-white p-2 font-mono text-[11px]">
                {report.errors.map((e, idx) => (
                  <div key={idx}>
                    <span className="text-b4l-red">rând {e.row}:</span> {e.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
